import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useId, useMemo, useState } from 'react';
import {
	AIPromptUsage,
	IAIPromptTemplate,
	filterAIPrompts,
	isAccountScopedPrompt,
} from '../../../../models/AIPromptTemplate';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAIPromptLibrary, useDeleteAIPrompt, useMyAIPrompts, useShareAIPrompt } from '../../../../queries/AIPrompt';
import { Button } from '@HoudiniUI/Button';
import { DeleteConfirmDialog } from '@HoudiniUI/DeleteConfirmDialog';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@HoudiniUI/Drawer';
import { InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupV2 } from '@HoudiniUI/InputGroup';
import { Label } from '@HoudiniUI/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@HoudiniUI/Select';
import { TabToggle } from '@HoudiniUI/TabToggle';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { SearchIcon2 } from '@WebComponents/svgs/icons/SearchIcon2';
import { XIconNew } from '@WebComponents/svgs/icons/XIcon';
import { PromptCard } from './PromptCard';
import { RenamePromptDialog } from './RenamePromptDialog';
import { styleSheet } from './styles';
import { usePromptIndustry } from './usePromptIndustry';

const ALL = 'all';
const SAMPLES = 'samples';
const SAVED = 'saved';
type PanelFilter = typeof ALL | typeof SAMPLES | typeof SAVED;

const FILTER_ITEMS = [
	{ label: 'All', value: ALL },
	{ label: 'Samples', value: SAMPLES },
	{ label: 'My prompts', value: SAVED },
];

/** On All, the team's section shows this many before a link to the rest, so the samples are not pushed out of view. */
const SAVED_PREVIEW_COUNT = 2;

const pluralize = (count: number, one: string) => `${count} ${one}${count === 1 ? '' : 's'}`;

interface IProps {
	/** What using a prompt does to the field, which depends on whether it already holds text. */
	description: string;
	impersonationContext?: Api.IImpersonationContext;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	onPromptSelected: (prompt: IAIPromptTemplate) => void;
	/** Shown under My prompts while the account has saved none for this usage. */
	savedEmptyText: string;
	showSamples?: boolean;
	usage: AIPromptUsage;
}

/**
 * Every prompt in a side panel: the team's saved prompts first, then Levitate's samples for an industry. Modal, with
 * the page blurred behind it as the audience filters drawer is, so choosing a prompt has the screen's attention;
 * picking one closes it and hands focus back to the field.
 */
export const AIPromptPanel: React.FC<IProps> = ({
	description,
	impersonationContext,
	isOpen,
	onOpenChange,
	onPromptSelected,
	savedEmptyText,
	showSamples: offersSamples = true,
	usage,
}) => {
	const id = useId();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const currentUserId = userSession?.user?.id;

	const [chosenFilter, setFilter] = useState<PanelFilter>(ALL);
	const filter = offersSamples ? chosenFilter : SAVED;
	const [searchInput, setSearchInput] = useState('');
	const [renaming, setRenaming] = useState<IAIPromptTemplate | null>(null);
	const [deleting, setDeleting] = useState<IAIPromptTemplate | null>(null);
	const { industry, industryName, items: industryItems, setIndustry } = usePromptIndustry();

	const showSaved = filter !== SAMPLES;
	const showSamples = filter !== SAVED;
	const mineQuery = useMyAIPrompts({ enabled: isOpen && showSaved, impersonationContext, usage });
	const libraryQuery = useAIPromptLibrary({ enabled: isOpen && showSamples, impersonationContext, industry, usage });

	const onError = (error: Api.IOperationResultNoValue) => errorMessages.pushApiError(error);
	const deletePrompt = useDeleteAIPrompt({ impersonationContext, onError, onSuccess: () => setDeleting(null) });
	const sharePrompt = useShareAIPrompt({ impersonationContext, onError });

	const search = searchInput.trim();
	const saved = useMemo(() => filterAIPrompts(mineQuery.data ?? [], search), [mineQuery.data, search]);
	const samples = useMemo(() => filterAIPrompts(libraryQuery.data ?? [], search), [libraryQuery.data, search]);
	const visibleSaved = filter === ALL ? saved.slice(0, SAVED_PREVIEW_COUNT) : saved;

	const use = (prompt: IAIPromptTemplate) => {
		onPromptSelected(prompt);
		onOpenChange(false);
	};

	const savedActions = {
		onDelete: setDeleting,
		onRename: setRenaming,
		onToggleShared: (prompt: IAIPromptTemplate) =>
			sharePrompt.mutate({
				id: prompt.id,
				scope: isAccountScopedPrompt(prompt) ? Api.TemplateScope.User : Api.TemplateScope.Account,
			}),
	};

	return (
		<>
			<Drawer onOpenChange={onOpenChange} open={isOpen} swipeDirection='right'>
				<DrawerContent>
					<DrawerHeader styles={[styleSheet.panelHeader]}>
						<div className={css(styleSheet.panelHeading)}>
							<DrawerTitle>Prompts</DrawerTitle>
							<DrawerDescription>{description}</DrawerDescription>
						</div>
						<DrawerClose
							render={props => (
								<Button aria-label='Close' size='icon' variant='ghost' {...props}>
									<XIconNew />
								</Button>
							)}
						/>
					</DrawerHeader>

					<div className={css(styleSheet.panelBody)}>
						{offersSamples ? (
							<TabToggle
								aria-label='Which prompts'
								items={FILTER_ITEMS}
								onValueChange={value => setFilter(value as PanelFilter)}
								size='small'
								value={filter}
							/>
						) : null}

						<InputGroupV2>
							<InputGroupAddon align='inlineStart'>
								<SearchIcon2 height={16} width={16} />
							</InputGroupAddon>
							<InputGroupInput
								aria-label='Search prompts'
								id={`${id}-prompt-search`}
								onChange={event => setSearchInput(event.target.value)}
								placeholder='Search by name or keyword'
								type='text'
								value={searchInput}
							/>
							{searchInput ? (
								<InputGroupAddon align='inlineEnd'>
									<InputGroupButton aria-label='Clear search' onClick={() => setSearchInput('')} size='iconXs'>
										<XIconNew />
									</InputGroupButton>
								</InputGroupAddon>
							) : null}
						</InputGroupV2>

						{showSaved ? (
							<section aria-label='My prompts' className={css(styleSheet.section)}>
								<div className={css(styleSheet.sectionHeader)}>
									<h3 className={css(styleSheet.sectionTitle)}>My prompts</h3>
									{mineQuery.data ? (
										<span className={css(styleSheet.sectionCount)}>{pluralize(saved.length, 'prompt')}</span>
									) : null}
								</div>
								<p className={css(styleSheet.sectionCaption)}>Saved by you and your team.</p>
								{mineQuery.isLoading ? <LoadingSpinner type='small' /> : null}
								{!mineQuery.isLoading && saved.length === 0 ? (
									<p className={css(styleSheet.sectionEmpty)}>
										{search ? `None of your prompts match “${search}”.` : savedEmptyText}
									</p>
								) : null}
								{visibleSaved.map(prompt => (
									<PromptCard
										currentUserId={currentUserId}
										key={prompt.id}
										onUse={use}
										prompt={prompt}
										savedActions={savedActions}
									/>
								))}
								{filter === ALL && saved.length > SAVED_PREVIEW_COUNT ? (
									<div>
										<Button onClick={() => setFilter(SAVED)} size='sm' variant='link'>
											{`Show all ${saved.length}`}
										</Button>
									</div>
								) : null}
							</section>
						) : null}

						{showSaved && showSamples ? <hr className={css(styleSheet.sectionDivider)} /> : null}

						{showSamples ? (
							<section aria-label='Samples from Levitate' className={css(styleSheet.section)}>
								<div className={css(styleSheet.sectionHeader)}>
									<h3 className={css(styleSheet.sectionTitle)}>Samples from Levitate</h3>
									{libraryQuery.data ? (
										<span className={css(styleSheet.sectionCount)}>
											{`${samples.length} for ${industryName.toLowerCase()}`}
										</span>
									) : null}
								</div>
								<p className={css(styleSheet.sectionCaption)}>
									Written by Levitate. Change the industry to see others.
								</p>
								<div className={css(styleSheet.field)}>
									<Label htmlFor={`${id}-industry`}>Industry</Label>
									<Select items={industryItems} onValueChange={setIndustry} value={industry}>
										<SelectTrigger id={`${id}-industry`} styles={[styleSheet.trigger]}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{industryItems.map(item => (
												<SelectItem key={`${id}-${item.value}`} value={item.value}>
													{item.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								{libraryQuery.isLoading ? <LoadingSpinner type='small' /> : null}
								{!libraryQuery.isLoading && samples.length === 0 ? (
									<p className={css(styleSheet.sectionEmpty)}>
										{search ? `No samples match “${search}”.` : 'There are no sample prompts for this industry yet.'}
									</p>
								) : null}
								{samples.map(prompt => (
									<PromptCard key={prompt.id} onUse={use} prompt={prompt} />
								))}
							</section>
						) : null}
					</div>
				</DrawerContent>
			</Drawer>

			<RenamePromptDialog
				impersonationContext={impersonationContext}
				onOpenChange={open => !open && setRenaming(null)}
				prompt={renaming}
			/>
			<DeleteConfirmDialog
				description='Anyone it was shared with loses it too. Campaigns already written from it are not affected.'
				isDeleting={deletePrompt.isPending}
				onClose={() => setDeleting(null)}
				onConfirm={() => deleting && deletePrompt.mutate(deleting.id)}
				open={!!deleting}
				title={deleting ? `Delete “${deleting.name}”?` : 'Delete this prompt?'}
			/>
		</>
	);
};
